import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface AspectRatioPreviewProps {
  aspectRatio: string;
}

/**
 * AspectRatioPreview - Visual preview of the selected aspect ratio
 *
 * Displays a visual representation of the chosen aspect ratio
 * to help users understand how their generated image will look.
 *
 * Replaces the Tips card on Step 1 of the multi-step workflow.
 */
export const AspectRatioPreview: React.FC<AspectRatioPreviewProps> = ({
  aspectRatio
}) => {
  // Calculate preview dimensions based on aspect ratio
  const getPreviewStyle = () => {
    switch (aspectRatio) {
      case '1:1':
        return { width: '200px', height: '200px' };
      case '4:5':
        return { width: '200px', height: '250px' };
      case '16:9':
        return { width: '320px', height: '180px' };
      case '9:16':
        return { width: '180px', height: '320px' };
      default:
        return { width: '200px', height: '200px' };
    }
  };

  const getAspectRatioName = () => {
    switch (aspectRatio) {
      case '1:1':
        return 'Square';
      case '4:5':
        return 'Vertical (Recommended)';
      case '16:9':
        return 'Landscape';
      case '9:16':
        return 'Portrait';
      default:
        return 'Unknown';
    }
  };

  const previewStyle = getPreviewStyle();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Preview</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center space-y-4">
        <div
          className="border-2 border-dashed border-primary bg-primary/5 rounded-lg flex items-center justify-center"
          style={previewStyle}
        >
          <div className="text-center space-y-1">
            <p className="text-lg font-semibold text-primary">{aspectRatio}</p>
            <p className="text-sm text-muted-foreground">{getAspectRatioName()}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground text-center max-w-xs">
          This is how your generated image will be proportioned
        </p>
      </CardContent>
    </Card>
  );
};

export default AspectRatioPreview;
